import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtDate } from "../lib/status";
import { X, CalendarClock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function toLocalInputParts(iso) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/**
 * Gap-closure item 3: Extend Rental. Only shown for `ongoing` bookings (see
 * BookingDetail.jsx's gating). Reuses the same real conflict engine
 * (POST /bookings/check-conflict) every other reschedule/reassign flow
 * uses - no ad hoc overlap math here. Sending the extension only creates a
 * pending request the renter still has to confirm (see
 * ExtensionPendingCard in CustomerConfirmation.jsx); this modal never
 * silently changes end_datetime itself.
 */
export default function ExtendRentalModal({ bookingId, booking, open, onClose, onDone }) {
  const initial = toLocalInputParts(booking?.end_datetime);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [note, setNote] = useState("");
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(null); // { has_critical, overlaps } | null
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const p = toLocalInputParts(booking?.end_datetime);
    setDate(p.date); setTime(p.time); setNote("");
    setChecked(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.end_datetime]);

  if (!open) return null;

  const newEndIso = date && time ? new Date(`${date}T${time}:00`).toISOString() : null;
  const isLaterThanCurrent = newEndIso && booking?.end_datetime && new Date(newEndIso) > new Date(booking.end_datetime);

  const checkAvailability = async () => {
    if (!newEndIso) { toast.error("Choose a new return date and time first."); return; }
    if (!isLaterThanCurrent) { toast.error("The new return must be later than the current return."); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/bookings/check-conflict", {
        item_id: booking.item_id, start_datetime: booking.end_datetime,
        end_datetime: newEndIso, exclude_id: bookingId,
      });
      setChecked(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setChecking(false);
    }
  };

  const send = async () => {
    if (!newEndIso) return;
    setSending(true);
    try {
      await api.post(`/bookings/${bookingId}/extend`, { new_end_datetime: newEndIso, note: note || undefined });
      toast.success("Extension confirmation sent to the customer");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const conflictBooking = checked?.overlaps?.[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }} data-testid="extend-rental-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#1B4332]" /> Extend Rental
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="extend-rental-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500">Current Return</div>
            <div className="font-medium mt-0.5">{fmtDate(booking?.end_datetime)}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">New Return Date</span>
              <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setChecked(null); }}
                     data-testid="extend-new-date"
                     className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">New Return Time</span>
              <input type="time" value={time} onChange={(e) => { setTime(e.target.value); setChecked(null); }}
                     data-testid="extend-new-time"
                     className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Optional note</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="extend-note"
                      className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </label>

          {checked && !checked.has_critical && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 inline-flex items-center gap-2" data-testid="extend-available">
              <CheckCircle2 className="w-4 h-4" /> Available for extension
            </div>
          )}

          {checked?.has_critical && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="extend-conflict">
              <div className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Extension Conflict</div>
              {conflictBooking && (
                <p className="mt-1">
                  {booking?.vehicle?.name || "This vehicle"} has another {conflictBooking.status} booking:{" "}
                  {fmtDate(conflictBooking.start_datetime)} · {conflictBooking.customer_name || "another customer"}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => setChecked(null)} data-testid="extend-choose-earlier"
                        className="px-3 py-1.5 rounded-md border border-red-300 bg-white text-xs hover:bg-red-100">
                  Choose Earlier Return
                </button>
                {conflictBooking && (
                  <Link to={`/app/bookings/${conflictBooking.id}`} data-testid="extend-view-next-booking"
                        className="px-3 py-1.5 rounded-md border border-red-300 bg-white text-xs hover:bg-red-100 inline-flex items-center">
                    View Next Booking
                  </Link>
                )}
              </div>
              <p className="mt-2 text-xs text-red-900/70">
                To offer a replacement vehicle or contact that customer, open the conflicting booking above.
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
            Cancel
          </button>
          {!checked || checked.has_critical ? (
            <button onClick={checkAvailability} disabled={checking || !newEndIso}
                    data-testid="extend-check-availability-btn"
                    className="px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              {checking ? "Checking…" : "Check Extension Availability"}
            </button>
          ) : (
            <button onClick={send} disabled={sending} data-testid="extend-send-confirmation-btn"
                    className="px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              {sending ? "Sending…" : "Send Extension Confirmation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
